import {
  WSButton,
  WSElement,
  WSMessageBox
} from "@wingspanhq/fe-component-library";
import React from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { Fullscreen } from "../../components/Fullscreen/Fullscreen";
import { useFeatureFlags } from "../../query/hooks/useFeatureFlags";
import {
  useCollaboratorsQuery,
  useInvoicesQuery,
  useMemberClientsQuery
} from "../../query/payments/queries";
import { getInvoice, getMemberClient } from "../../query/payments/selectors";
import { WSQueries } from "../../query/WSQuery";
import { InvoicesForm } from "../components/InvoicesForm/InvoicesForm";
import { useDefaultInvoiceSubject } from "../utils";
import {
  attachmentsToFormData,
  collaboratorToFormData,
  convertToLineItemFormData,
  defaultFrequency,
  lateFeeToFormData,
  paymentMethodsToFormData
} from "../utils/invoicesFormUtils";

type Props = RouteComponentProps<{ invoiceId: string }>;

export const InvoicesInvoiceEdit: React.FC<Props> = ({ match }) => {
  const history = useHistory();
  const memberClientsQuery = useMemberClientsQuery();
  const collaboratorsQuery = useCollaboratorsQuery();
  const qFeatureFlags = useFeatureFlags();
  const invoicesQuery = useInvoicesQuery();
  const invoiceId = match.params.invoiceId;
  const defaultSubject = useDefaultInvoiceSubject();

  return (
    <WSQueries
      queries={{
        memberClientsQuery,
        invoicesQuery,
        collaboratorsQuery,
        qFeatureFlags
      }}
    >
      {({
        memberClientsQuery: { data: memberClients },
        invoicesQuery: { data: invoices },
        collaboratorsQuery: { data: collaborators },
        qFeatureFlags: { data: featureFlags }
      }) => {
        const invoice = getInvoice(invoices, invoiceId);
        const memberClient = invoice
          ? getMemberClient(memberClients, invoice.memberClientId as string)
          : undefined;

        if (invoice) {
          return (
            <InvoicesForm
              context={{
                invoice
              }}
              defaultValues={{
                subject: String(invoice.labels?.subject || defaultSubject),
                client: {
                  memberClientId: memberClient?.memberClientId || "",
                  company: memberClient?.company || "",
                  contact: memberClient?.name || "",
                  contactEmail: memberClient?.emailTo || "",
                  contactEmailsCC: (memberClient?.emailCC || []).map(email => ({
                    email
                  }))
                },
                ...(featureFlags.purchaseOrderNumber
                  ? {
                      purchaseOrderNumber:
                        invoice.metadata?.purchaseOrderNumber || ""
                    }
                  : {}),
                lineItems: (invoice?.lineItems || []).map(
                  convertToLineItemFormData
                ),
                other: {
                  projectName: String(invoice?.labels?.projectName || ""),
                  notes: invoice?.invoiceNotes || "",
                  attachments: attachmentsToFormData(
                    invoice?.attachments?.customAttachmentIds
                  )
                },
                due: "custom",
                customDueDate: invoice?.dueDate || new Date(),
                includesLateFee:
                  !!invoice.lateFeeHandling &&
                  !!invoice.lateFeeHandling?.frequency,
                lateFee: lateFeeToFormData(invoice.lateFeeHandling),
                send: {
                  type: "immediately",
                  date: new Date()
                },
                recurring: false,
                frequency: defaultFrequency,
                collaborators: collaboratorToFormData(
                  invoice.collaborators || [],
                  collaborators
                ),
                paymentMethods: paymentMethodsToFormData(
                  invoice.acceptedPaymentMethods
                ),
                advanced: {
                  sendEmails: !!invoice?.notificationPreferences?.sendInvoice,
                  sendReminders: !!invoice?.notificationPreferences
                    ?.sendReminders,
                  clientPaysProcessingFees:
                    invoice?.creditFeeHandling?.memberPays !== 100
                }
              }}
            />
          );
        } else {
          return (
            <Fullscreen>
              <WSElement p="M">
                <WSMessageBox.Error mb="M">
                  No such invoice found
                </WSMessageBox.Error>
                <WSButton
                  onClick={() => {
                    history.push("/member/invoices");
                  }}
                >
                  Go back
                </WSButton>
              </WSElement>
            </Fullscreen>
          );
        }
      }}
    </WSQueries>
  );
};
